import { Form, Input } from "decorator-form";
import React from "react";
import styled from "styled-components";

import { IKeyValuePair } from "common/form/fields/KeyValueListField";
import InputType from "common/form/InputType";
import { BlockView } from "common/views/block/BlockView.view";
import HeadingView from "common/views/heading/Heading.view";
import { ImageView } from "common/views/image/Image.view";
import TypographyView from "common/views/typographyView/Typography.view";
import Label from "core/components/label/Label";
import { IPanelProps, Panel } from "core/decorators/Panel";
import Logger from "core/util/Logger";

const Wrapper = styled.div`
	@media (min-width: 991px) {
		display: flex;
		flex-direction: row;

		& > div {
			min-width: 33.3333333%;
			max-width: 33.3333333%;
		}
	}
`;

@Form({title: "Intel Support Blade"})
class IntelSupportBladeConfig {

	@Input({type: InputType.TEXT, title: "Organization ID"})
	public organizationId: string;

	@Input({type: InputType.NUMBER_POSITIVE, title: "Chat Window Width"})
	public chatWidth: number = 800;

	@Input({type: InputType.NUMBER_POSITIVE, title: "Chat Window Height"})
	public chatHeight: number = 1000;

	@Input({type: InputType.TEXT, title: "Support Icon URL"})
	public supportIconUrl: string;

	@Input({type: InputType.TEXT, title: "Chat Icon URL"})
	public chatIconUrl: string;

	@Input({type: InputType.TEXT, title: "Phone Icon URL"})
	public phoneIconUrl: string;

	@Input({
		type: InputType.KEY_VALUE_LIST_FLAT,
		title: "Support Links"
	})
	public supportLinks: IKeyValuePair[];

	@Input({
		type: InputType.KEY_VALUE_LIST_FLAT,
		title: "Chat IDs"
	})
	public chatIds: IKeyValuePair[];

	@Input({
		type: InputType.KEY_VALUE_LIST_FLAT,
		title: "Chart Init IDs"
	})
	public chatInitIds: IKeyValuePair[];

	@Input({
		type: InputType.KEY_VALUE_LIST_FLAT,
		title: "Chart Organization IDs"
	})
	public chatOrganizationIds: IKeyValuePair[];

	@Input({
		type: InputType.KEY_VALUE_LIST_FLAT,
		title: "Phone Links"
	})
	public phoneLinks: IKeyValuePair[];

}

@Panel({id: "intelrco-support-blade", configClass: IntelSupportBladeConfig})
class IntelSupportBladePanel extends React.Component<IPanelProps<IntelSupportBladeConfig>> {

	private chatRef: React.RefObject<HTMLAnchorElement>;
	private chatUnavailableRef: React.RefObject<HTMLSpanElement>;

	private chatIds = {
		de_DE: "573U0000000ChXg",
		en_US: "573U0000000ChXS",
		es_AR: "573U0000000ChXm",
		es_ES: "573U0000000ChXm",
		fr_FR: "573U0000000ChXe",
		in_ID: "573U0000000ChXS",
		it_IT: "573U0000000ChXk",
		ja_JP: "5730P000000TUdT",
		ko_KR: "5730P0000004Hhl",
		pl_PL: "573U0000000ChXS",
		pt_BR: "573U0000000ChXl",
		ru_RU: "573U0000000ChXt",
		th_TH: "573U0000000ChXS",
		tr_TR: "573U0000000ChXS",
		vi_VN: "573U0000000ChXS",
		zh_CN: "573U0000000Chem",
		zh_TW: "573U0000000Cher"
	};

	private supportLinks = {
		de_DE: "https://supporttickets.intel.com/supportrequest?lang=de-DE&productId=198278%3A10849",
		en_US: "https://supporttickets.intel.com/supportrequest?lang=en-US&productId=198278%3A10849",
		es_AR: "https://supporttickets.intel.com/supportrequest?lang=es-ES&productId=198278%3A10849",
		es_ES: "https://supporttickets.intel.com/supportrequest?lang=es-ES&productId=198278%3A10849",
		fr_FR: "https://supporttickets.intel.com/supportrequest?lang=fr-FR&productId=198278%3A10849",
		in_ID: "https://supporttickets.intel.com/supportrequest?lang=en-US&productId=198278%3A10849",
		it_IT: "https://supporttickets.intel.com/supportrequest?lang=it-IT&productId=198278%3A10849",
		ja_JP: "https://supporttickets.intel.com/supportrequest?lang=ja-JP&productId=198278%3A10849",
		ko_KR: "https://supporttickets.intel.com/supportrequest?lang=en-US&productId=198278%3A10849",
		pl_PL: "https://supporttickets.intel.com/supportrequest?lang=en-US&productId=198278%3A10849",
		pt_BR: "https://supporttickets.intel.com/supportrequest?lang=pt-BR&productId=198278%3A10849",
		ru_RU: "https://supporttickets.intel.com/supportrequest?lang=ru-RU&productId=198278%3A10849",
		th_TH: "https://supporttickets.intel.com/supportrequest?lang=en-US&productId=198278%3A10849",
		tr_TR: "https://supporttickets.intel.com/supportrequest?lang=en-US&productId=198278%3A10849",
		vi_VN: "https://supporttickets.intel.com/supportrequest?lang=en-US&productId=198278%3A10849",
		zh_CN: "https://supporttickets.intel.com/supportrequest?lang=zh-CN&productId=198278%3A10849",
		zh_TW: "https://supporttickets.intel.com/supportrequest?lang=zh-TW&productId=198278%3A10849"
	};

	private phoneLinks = {
		de_DE: "https://www.intel.com/content/www/de/de/secure/partner/support/partner-phone-list.html",
		en_US: "https://www.intel.com/content/www/us/en/secure/partner/support/partner-phone-list.html",
		es_AR: "https://www.intel.com/content/www/xl/es/secure/partner/support/partner-phone-list.html",
		es_ES: "https://www.intel.com/content/www/es/es/secure/partner/support/partner-phone-list.html",
		fr_FR: "https://www.intel.com/content/www/fr/fr/secure/partner/support/partner-phone-list.html",
		in_ID: "https://www.intel.com/content/www/us/en/secure/partner/support/partner-phone-list.html",
		it_IT: "https://www.intel.com/content/www/it/it/secure/partner/support/partner-phone-list.html",
		ja_JP: "https://www.intel.com/content/www/jp/ja/secure/partner/support/partner-phone-list.html",
		ko_KR: "https://www.intel.com/content/www/kr/ko/secure/partner/support/partner-phone-list.html",
		pl_PL: "https://www.intel.com/content/www/pl/pl/secure/partner/support/partner-phone-list.html",
		pt_BR: "https://www.intel.com/content/www/br/pt/secure/partner/support/partner-phone-list.html",
		ru_RU: "https://www.intel.com/content/www/ru/ru/secure/partner/support/partner-phone-list.html",
		th_TH: "https://www.intel.com/content/www/th/th/secure/partner/support/partner-phone-list.html",
		tr_TR: "https://www.intel.com/content/www/tr/tr/secure/partner/support/partner-phone-list.html",
		vi_VN: "https://www.intel.com/content/www/us/en/secure/partner/support/partner-phone-list.html",
		zh_CN: "https://www.intel.com/content/www/cn/zh/secure/partner/support/partner-phone-list.html",
		zh_TW: "https://www.intel.com/content/www/tw/zh/secure/partner/support/partner-phone-list.html"
	};

	public constructor(props: IPanelProps<IntelSupportBladeConfig>) {
		super(props);

		this.chatRef = React.createRef();
		this.chatUnavailableRef = React.createRef();
	}

	public render() {
		const { config, locale } = this.props;

		let supportLink: string;
		if (config.supportLinks) {
			for (const kv of config.supportLinks) {
				if (locale === kv.key && typeof kv.value === 'string') {
					supportLink = kv.value;
					break;
				}
			}
		}
		if (!supportLink) {
			supportLink = this.supportLinks[locale] || this.supportLinks['en_US'];
		}

		let phoneLink: string;
		if (config.phoneLinks) {
			for (const kv of config.phoneLinks) {
				if (locale === kv.key && typeof kv.value === 'string') {
					phoneLink = kv.value;
					break;
				}
			}
		}
		if (!phoneLink) {
			phoneLink = this.phoneLinks[locale] || this.phoneLinks['en_US'];
		}

		return (
			<Wrapper>
				{
					[
						{
							key: 'support',
							iconUrl: config.supportIconUrl,
							title: 'Request Support',
							description: 'Contact us anytime, we will respond within 1 business day',
							link: (
								<a href={supportLink}>
									<Label k="intelrco.support.blade.support.link" v="Submit Request" />
								</a>
							)
						},
						{
							key: 'chat',
							iconUrl: config.chatIconUrl,
							title: 'Chat',
							description: 'Chat available during normal business hour',
							link: (
								<React.Fragment>
									<a ref={this.chatRef} href="javascript://Chat" onClick={this.startChat}>
										<Label k="intelrco.support.blade.chat.available" v="Chat Now" />
									</a>
									<span ref={this.chatUnavailableRef}>
										<Label k="intelrco.support.blade.chat.unavailable" v="Chat Not Available" />
									</span>
								</React.Fragment>
							)
						},
						{
							key: 'phone',
							iconUrl: config.phoneIconUrl,
							title: 'Phone',
							description: 'Call us during normal business hours',
							link: (
								<a href={phoneLink}>
									<Label k="intelrco.support.blade.phone.link" v="View Phone Directory" />
								</a>
							)
						}
					].map((section) => (
						<BlockView viewKey="section" key={section.key} style={{display: 'flex'}}>
							<ImageView viewKey="icon" src={section.iconUrl} style={{marginRight: '10px'}} />
							<div>
								<HeadingView viewKey="title" style={{marginBottom: '10px'}}>
									<Label k={`intelrco.support.blade.${section.key}.title`} v={section.title} />
								</HeadingView>
								<TypographyView viewKey="description" style={{marginBottom: '10px'}}>
									<Label k={`intelrco.support.blade.${section.key}.description`} v={section.description} />
								</TypographyView>
								{section.link}
							</div>
						</BlockView>
					))
				}
			</Wrapper>
		);
	}

	public componentDidMount() {
		let script: HTMLScriptElement = document.getElementById("intelrco:chat") as HTMLScriptElement;

		if (!script) {
			script = document.createElement('script');
			const head = document.getElementsByTagName('head')[0];
			head.appendChild(script);
			script.onload = this.initChat;
			script.type = "text/javascript";
			script.src = "https://c.la2-c2-dfw.salesforceliveagent.com/content/g/js/39.0/deployment.js";
		} else {
			this.initChat();
		}
	}

	public componentDidUpdate(prevProps: IPanelProps<IntelSupportBladeConfig>) {
		if (prevProps.locale !== this.props.locale) {
			this.initChat();
		}
	}

	private initChat = () => {
		if (window['liveagent']) {

			try {
				if (!window['_laq']) {
					window['_laq'] = [];
				}

				const { config, locale } = this.props;

				window['liveagent'].setChatWindowWidth(config.chatWidth || 800);
				window['liveagent'].setChatWindowHeight(config.chatHeight || 1000);

				let chatId: string;
				if (config.chatIds) {
					for (const kv of config.chatIds) {
						if (locale === kv.key && typeof kv.value === 'string') {
							chatId = kv.value;
							break;
						}
					}
				}
				if (!chatId) {
					chatId = this.chatIds[locale] || this.chatIds['en_US'];
				}

				Logger.debug("Initializing Intel Support Bar Chat: [chatId=%s,locale=%s]", chatId, locale);

				window['_laq'].push(() => {
					window['liveagent'].showWhenOnline(chatId, this.chatRef.current);
					window['liveagent'].showWhenOffline(chatId, this.chatUnavailableRef.current);
				});

				let organizationId: string;
				if (config.chatOrganizationIds) {
					for (const kv of config.chatOrganizationIds) {
						if (locale === kv.key && typeof kv.value === 'string') {
							organizationId = kv.value;
							break;
						}
					}
				}
				if (!organizationId) {
					organizationId = '00DU0000000YT3c';
				}

				let chatInitId: string;
				if (config.chatInitIds) {
					for (const kv of config.chatInitIds) {
						if (locale === kv.key && typeof kv.value === 'string') {
							chatId = kv.value;
							break;
						}
					}
				}
				if (!chatInitId) {
					chatInitId = '572U0000000CgOI';
				}

				window['liveagent'].init('https://c.la2-c2-dfw.salesforceliveagent.com/chat', chatInitId, organizationId);
			} catch (e) {
				Logger.error("Failed to initialize chat");
			}
		} else {
			Logger.debug("Unable to initialize Intel Support Bar");
		}
	}

	private startChat = () => {
		if (window['liveagent']) {
			try {
				const { config, locale } = this.props;

				let chatId: string;
				if (config.chatIds) {
					for (const kv of config.chatIds) {
						if (locale === kv.key && typeof kv.value === 'string') {
							chatId = kv.value;
							break;
						}
					}
				}
				if (!chatId) {
					chatId = this.chatIds[locale] || this.chatIds['en_US'];
				}

				window['liveagent'].startChat(chatId);
			} catch (e) {
				Logger.error("Error while starting chat.");
			}
		}
	}
}



// WEBPACK FOOTER //
// ./src/panels/client/intelrco/IntelSupportBlade.panel.tsx